import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createMcpServer,
  deleteMcpServer,
  listMcpServers,
  testMcpServer,
  updateMcpServer
} from '../../utils/api';

const EMPTY_FORM = {
  name: '',
  transport: 'stdio',
  command: '',
  arguments: '',
  url: '',
  authType: 'none',
  authSecret: '',
  env: '{}',
  workingDirectory: '',
  timeoutMs: 30000,
  readOnly: false,
  isPublic: false,
  isActive: true,
  scopeProjectId: ''
};

function parseEnvInput(raw) {
  const text = String(raw || '').trim();
  if (!text) return {};
  const parsed = JSON.parse(text);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Environment variables must be a JSON object');
  }
  return parsed;
}

export function SettingsMcpPanel({
  feelLuckyMcpServerId = '',
  onFeelLuckyMcpServerChange,
  feelLuckyMcpDirty = false,
  disabled = false
}) {
  const [loading, setLoading] = useState(true);
  const [owned, setOwned] = useState([]);
  const [shared, setShared] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [lastTestById, setLastTestById] = useState({});
  const [clearAuthSecret, setClearAuthSecret] = useState(false);

  const selectableServers = useMemo(
    () => [...owned, ...shared].filter((s) => s.isActive),
    [owned, shared]
  );

  const loadServers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMcpServers();
      setOwned(data.owned || []);
      setShared(data.public || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load MCP servers' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
    setClearAuthSecret(false);
  };

  const startEdit = (server) => {
    setEditingId(server.id);
    setShowForm(true);
    setForm({
      name: server.name || '',
      transport: server.transport || 'stdio',
      command: server.command || '',
      arguments: Array.isArray(server.arguments) ? server.arguments.join(', ') : '',
      url: server.url || '',
      authType: server.authType || 'none',
      authSecret: '',
      env: '{}',
      workingDirectory: server.workingDirectory || '',
      timeoutMs: server.timeoutMs || 30000,
      readOnly: !!server.readOnly,
      isPublic: !!server.isPublic,
      isActive: server.isActive !== false,
      scopeProjectId: server.scopeProjectId || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        name: form.name,
        transport: form.transport,
        command: form.command,
        arguments: form.arguments,
        url: form.url,
        authType: form.authType,
        workingDirectory: form.workingDirectory,
        timeoutMs: Number(form.timeoutMs) || 30000,
        readOnly: form.readOnly,
        isPublic: form.isPublic,
        isActive: form.isActive,
        scopeProjectId: form.scopeProjectId,
        env: parseEnvInput(form.env)
      };
      if (form.authSecret.trim()) payload.authSecret = form.authSecret.trim();
      if (clearAuthSecret) {
        payload.authSecret = '';
        payload.clearAuthSecret = true;
      }

      if (editingId) {
        await updateMcpServer(editingId, payload);
        setMessage({ type: 'success', text: 'MCP server updated' });
      } else {
        await createMcpServer(payload);
        setMessage({ type: 'success', text: 'MCP server created' });
      }
      resetForm();
      await loadServers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save MCP server' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serverId) => {
    if (!window.confirm('Delete this MCP server?')) return;
    try {
      await deleteMcpServer(serverId);
      if (feelLuckyMcpServerId === serverId) onFeelLuckyMcpServerChange?.('');
      await loadServers();
      setMessage({ type: 'success', text: 'MCP server deleted' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete MCP server' });
    }
  };

  const handleTest = async (serverId) => {
    setTestingId(serverId);
    setMessage(null);
    try {
      const result = await testMcpServer(serverId);
      setLastTestById((prev) => ({ ...prev, [serverId]: { ok: true, ...result } }));
      setMessage({
        type: 'success',
        text: `MCP test OK — ${result.toolCount ?? 0} tool(s) in ${result.durationMs ?? '?'} ms`
      });
    } catch (err) {
      setLastTestById((prev) => ({
        ...prev,
        [serverId]: { ok: false, error: err.message || 'MCP test failed' },
      }));
      setMessage({ type: 'error', text: err.message || 'MCP test failed' });
    } finally {
      setTestingId(null);
    }
  };

  const renderServerRow = (server, { canEdit }) => {
    const debug = lastTestById[server.id];
    return (
      <div className="user-provider-row mcp-server-row" key={server.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: '100%' }}>
          <div className="user-provider-row-info">
            <div className="setting-label">
              {server.name}
              {server.isPublic && <span className="badge badge-user" style={{ marginLeft: 8 }}>PUBLIC</span>}
              {!server.isActive && <span className="badge" style={{ marginLeft: 8 }}>OFF</span>}
            </div>
            <div className="setting-description">
              {server.transport.toUpperCase()} · {server.slug}
              {server.scopeProjectId ? ` · scope ${server.scopeProjectId}` : ''}
              {server.hasAuthSecret ? ` · auth ${server.maskedAuthSecret || '••••'}` : ''}
            </div>
          </div>
          <div className="user-provider-row-input mcp-server-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleTest(server.id)} disabled={disabled || testingId === server.id}>
              {testingId === server.id ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-plug" /> Test MCP</>}
            </button>
            {canEdit && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => startEdit(server)} disabled={disabled}>Edit</button>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(server.id)} disabled={disabled}>Delete</button>
              </>
            )}
          </div>
        </div>
        {debug && (
          <pre style={{
            marginTop: 10,
            maxHeight: 200,
            overflow: 'auto',
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 8,
            borderRadius: 8,
          }}
          >
{JSON.stringify(
  debug.ok === false
    ? { success: false, error: debug.error }
    : {
        success: true,
        toolCount: debug.toolCount,
        durationMs: debug.durationMs,
        transport: debug.transport,
        tools: (debug.tools || []).map((t) => ({
          name: t.name,
          description: t.description || '',
        })),
      },
  null,
  2
)}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="settings-mcp-panel">
      {message && (
        <div className={`api-key-status ${message.type === 'error' ? 'api-key-pending' : 'api-key-saved'}`}>
          {message.text}
        </div>
      )}

      {selectableServers.length > 0 && (
        <div className="setting-item mcp-feel-lucky-picker">
          <div>
            <div className="setting-label">I feel lucky — prefer MCP before scrape</div>
            <div className="setting-description">
              When enabled, web search tries this MCP server first, then falls back to HTML scraping.
            </div>
          </div>
          <div className="setting-value">
            <select
              className="form-control"
              value={feelLuckyMcpServerId || ''}
              onChange={(e) => onFeelLuckyMcpServerChange?.(e.target.value)}
              disabled={disabled}
            >
              <option value="">None (scrape only)</option>
              {selectableServers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.isPublic && !owned.some((o) => o.id === s.id) ? ' (shared)' : ''}
                </option>
              ))}
            </select>
            {feelLuckyMcpDirty && (
              <div className="api-key-status api-key-pending">Unsaved — click Save User Settings</div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading"><i className="fas fa-spinner fa-spin" /> Loading MCP servers...</div>
      ) : (
        <>
          <div className="mcp-server-section">
            <div className="mcp-section-header">
              <h4>Your MCP servers</h4>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { resetForm(); setShowForm(true); }} disabled={disabled}>
                <i className="fas fa-plus" /> Add MCP server
              </button>
            </div>
            <div className="user-provider-list">
              {owned.length === 0 ? <p className="section-description">No MCP servers yet.</p> : owned.map((s) => renderServerRow(s, { canEdit: true }))}
            </div>
          </div>

          {shared.length > 0 && (
            <div className="mcp-server-section">
              <h4>Shared MCP servers (public)</h4>
              <p className="section-description">Available to everyone for MCP tooling and feel-lucky search.</p>
              <div className="user-provider-list">
                {shared.map((s) => renderServerRow(s, { canEdit: false }))}
              </div>
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="mcp-server-form">
          <h4>{editingId ? 'Edit MCP server' : 'New MCP server'}</h4>
          <div className="mcp-form-grid">
            <label>Name<input className="form-control" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} disabled={saving || disabled} /></label>
            <label>Transport
              <select className="form-control" value={form.transport} onChange={(e) => setForm((f) => ({ ...f, transport: e.target.value }))} disabled={saving || disabled}>
                <option value="stdio">stdio</option>
                <option value="sse">sse</option>
                <option value="http">http</option>
              </select>
            </label>
            {form.transport === 'stdio' ? (
              <>
                <label>Command<input className="form-control" value={form.command} onChange={(e) => setForm((f) => ({ ...f, command: e.target.value }))} placeholder="node, python, uvx" disabled={saving || disabled} /></label>
                <label>Arguments (comma-separated)<input className="form-control" value={form.arguments} onChange={(e) => setForm((f) => ({ ...f, arguments: e.target.value }))} disabled={saving || disabled} /></label>
                <label>Working directory<input className="form-control" value={form.workingDirectory} onChange={(e) => setForm((f) => ({ ...f, workingDirectory: e.target.value }))} disabled={saving || disabled} /></label>
              </>
            ) : (
              <label>URL<input className="form-control" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://..." disabled={saving || disabled} /></label>
            )}
            <label>Auth type
              <select className="form-control" value={form.authType} onChange={(e) => setForm((f) => ({ ...f, authType: e.target.value }))} disabled={saving || disabled}>
                <option value="none">none</option>
                <option value="bearer">Bearer</option>
                <option value="api_key">API key</option>
                <option value="oauth">OAuth</option>
              </select>
            </label>
            <label>API key / token (optional)
              <input
                type="password"
                className="form-control"
                value={form.authSecret}
                onChange={(e) => {
                  setClearAuthSecret(false);
                  setForm((f) => ({ ...f, authSecret: e.target.value }));
                }}
                placeholder={editingId ? 'Leave blank to keep existing' : ''}
                disabled={saving || disabled || clearAuthSecret}
                autoComplete="off"
              />
            </label>
            {editingId ? (
              <label className="mcp-checkbox">
                <input
                  type="checkbox"
                  checked={clearAuthSecret}
                  onChange={(e) => {
                    setClearAuthSecret(e.target.checked);
                    if (e.target.checked) setForm((f) => ({ ...f, authSecret: '' }));
                  }}
                  disabled={saving || disabled}
                />
                Clear saved API key / token
              </label>
            ) : null}
            <label>Timeout (ms)<input type="number" className="form-control" value={form.timeoutMs} onChange={(e) => setForm((f) => ({ ...f, timeoutMs: e.target.value }))} disabled={saving || disabled} /></label>
            <label>Scope / project ID
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-control" value={form.scopeProjectId} onChange={(e) => setForm((f) => ({ ...f, scopeProjectId: e.target.value }))} disabled={saving || disabled} />
                {Boolean(form.scopeProjectId) && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm((f) => ({ ...f, scopeProjectId: '' }))} disabled={saving || disabled}>×</button>
                )}
              </div>
            </label>
            <label className="mcp-form-full">Environment variables (JSON object)
              <textarea className="form-control" rows={3} value={form.env} onChange={(e) => setForm((f) => ({ ...f, env: e.target.value }))} disabled={saving || disabled} />
            </label>
            <label className="mcp-checkbox"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} disabled={saving || disabled} /> Active</label>
            <label className="mcp-checkbox"><input type="checkbox" checked={form.isPublic} onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))} disabled={saving || disabled} /> Public (share with everyone)</label>
            <label className="mcp-checkbox"><input type="checkbox" checked={form.readOnly} onChange={(e) => setForm((f) => ({ ...f, readOnly: e.target.checked }))} disabled={saving || disabled} /> Read-only mode</label>
          </div>
          <div className="mcp-form-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || disabled || !form.name.trim()}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Saving...</> : 'Save MCP server'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
